import { Dayjs } from 'dayjs'

// Time period types
export type TimePeriod = 'TODAY' | 'UPCOMING' | 'PAST'

// Membership type for filtering
export type MembershipType = 'both' | 'following' | 'permission'

// Sort order type
export type SortOrder = 'asc' | 'desc'

// Entity types
export interface Organization {
  id: string
  name: string
  name_full: string
  abbreviation?: string
  image?: any
  is_favorite?: boolean
}

export interface TreeScheduleNode {
  id: string
  name: string
  name_full: string
  starts_at: string
  ends_at: string
  image_url: string | null
  membership?: {
    role_id: number
    role_name: string
  }
}

export interface OrganizationTreeNode {
  id: string
  name: string
  name_full: string
  abbreviation: string
  image_url: string | null
  organization_type_id: number
  schedule_type_id?: number
  children_count: number
  children?: OrganizationTreeNode[]
  schedules?: TreeScheduleNode[]
  membership?: {
    role_id: number
    role_name: string
  }
}

export interface Schedule {
  id: string
  name: string
  name_full: string
  starts_at: string
  ends_at: string
  sport_id: string
  organization_id: string
  organization?: Organization
  is_favorite?: boolean
  image?: any
}

export interface Team {
  id: string
  name: string
  name_full: string
  abbreviation?: string
  sport_id: string
  organization_id: string
  image?: any
  is_favorite?: boolean
  organization?: Organization
}

export interface Venue {
  id: string
  name: string
  address?: any
  city?: string
  state?: string
  timezone?: string
  facilities?: Facility[]
}

export interface Facility {
  id: string
  name: string
  venue_id: string
}

export interface Game {
  id: string
  starts_at: string
  ends_at: string
  started_at?: string
  ended_at?: string
  name?: string
  description?: string
  sport_id: string
  generic_sport_id?: string
  venue?: Venue
  facility?: Facility
  schedule?: Schedule
  rootSchedule?: Schedule
  home_team?: Team
  away_team?: Team
  home_score?: number
  away_score?: number
  game_status_type_id?: number
  game_type_id?: number
}

export interface Pagination {
  total: number
  count: number
  per_page: number
  current_page: number
  total_pages: number
}

// State interface
export interface MyScheduleState {
  // Filter selections
  selectedOrganization: Organization | null
  selectedSchedule: Schedule | null
  selectedTeam: Team | null
  selectedVenue: Venue | null
  selectedFacility: Facility | null
  selectedDateRange: [Dayjs, Dayjs] | null
  selectedTimePeriod: TimePeriod
  selectedMembershipType: MembershipType
  selectedSortOrder: SortOrder
  calendarMode: boolean

  // Filter options
  organizationsTree: OrganizationTreeNode[] | null
  schedules: Schedule[]
  teams: Team[]
  venues: Venue[]

  // Filter pagination
  schedulesPagination: Pagination | null
  teamsPagination: Pagination | null
  venuesPagination: Pagination | null

  // Filter search queries
  schedulesSearchQuery: string
  teamsSearchQuery: string
  venuesSearchQuery: string

  // Filter appending states
  appendingSchedules: boolean
  appendingTeams: boolean
  appendingVenues: boolean

  // Games data
  games: Game[]
  gamesPagination: Pagination | null

  // Dates data
  dates: string[]
  hasTodayGames: boolean
  hasUpcomingGames: boolean
  datesLoaded: boolean
  /**
   * False until the first games fetch has resolved. Distinct from
   * `loadingGames`: that is false both BEFORE a fetch starts and after it
   * ends, so it cannot tell "no games" from "not asked yet".
   */
  gamesFetched: boolean

  // Loading states
  loadingGames: boolean
  loadingOrganizations: boolean
  loadingSchedules: boolean
  loadingTeams: boolean
  loadingVenues: boolean
  loadingDates: boolean
  filtersLoaded: boolean
}

// Action types
export enum ActionType {
  SET_SELECTED_ORGANIZATION = 'SET_SELECTED_ORGANIZATION',
  SET_SELECTED_SCHEDULE = 'SET_SELECTED_SCHEDULE',
  SET_SELECTED_TEAM = 'SET_SELECTED_TEAM',
  SET_SELECTED_VENUE = 'SET_SELECTED_VENUE',
  SET_SELECTED_FACILITY = 'SET_SELECTED_FACILITY',
  SET_SELECTED_DATE_RANGE = 'SET_SELECTED_DATE_RANGE',
  SET_SELECTED_TIME_PERIOD = 'SET_SELECTED_TIME_PERIOD',
  SET_ORGANIZATIONS_TREE = 'SET_ORGANIZATIONS_TREE',
  SET_SCHEDULES = 'SET_SCHEDULES',
  SET_TEAMS = 'SET_TEAMS',
  SET_VENUES = 'SET_VENUES',
  SET_GAMES = 'SET_GAMES',
  SET_LOADING_GAMES = 'SET_LOADING_GAMES',
  SET_LOADING_ORGANIZATIONS = 'SET_LOADING_ORGANIZATIONS',
  SET_LOADING_SCHEDULES = 'SET_LOADING_SCHEDULES',
  SET_LOADING_TEAMS = 'SET_LOADING_TEAMS',
  SET_LOADING_VENUES = 'SET_LOADING_VENUES',
  SET_LOADING_DATES = 'SET_LOADING_DATES',
  SET_DATES = 'SET_DATES',
  CLEAR_FILTER = 'CLEAR_FILTER',
  CLEAR_ALL_FILTERS = 'CLEAR_ALL_FILTERS',
  SET_FILTERS_LOADED = 'SET_FILTERS_LOADED',
  APPEND_GAMES = 'APPEND_GAMES',
  SET_SELECTED_MEMBERSHIP_TYPE = 'SET_SELECTED_MEMBERSHIP_TYPE',
  // Pagination actions for filter dropdowns
  APPEND_SCHEDULES = 'APPEND_SCHEDULES',
  APPEND_TEAMS = 'APPEND_TEAMS',
  APPEND_VENUES = 'APPEND_VENUES',
  SET_APPENDING_SCHEDULES = 'SET_APPENDING_SCHEDULES',
  SET_APPENDING_TEAMS = 'SET_APPENDING_TEAMS',
  SET_APPENDING_VENUES = 'SET_APPENDING_VENUES',
  SET_SCHEDULES_SEARCH_QUERY = 'SET_SCHEDULES_SEARCH_QUERY',
  SET_TEAMS_SEARCH_QUERY = 'SET_TEAMS_SEARCH_QUERY',
  SET_VENUES_SEARCH_QUERY = 'SET_VENUES_SEARCH_QUERY',
  SET_SELECTED_SORT_ORDER = 'SET_SELECTED_SORT_ORDER',
  SET_CALENDAR_MODE = 'SET_CALENDAR_MODE',
}

// Action interfaces
export type MyScheduleAction =
  | { type: ActionType.SET_SELECTED_ORGANIZATION; payload: Organization | null }
  | { type: ActionType.SET_SELECTED_SCHEDULE; payload: Schedule | null }
  | { type: ActionType.SET_SELECTED_TEAM; payload: Team | null }
  | { type: ActionType.SET_SELECTED_VENUE; payload: Venue | null }
  | { type: ActionType.SET_SELECTED_FACILITY; payload: Facility | null }
  | { type: ActionType.SET_SELECTED_DATE_RANGE; payload: [Dayjs, Dayjs] | null }
  | { type: ActionType.SET_SELECTED_TIME_PERIOD; payload: TimePeriod }
  | {
      type: ActionType.SET_ORGANIZATIONS_TREE
      payload: OrganizationTreeNode[] | null
    }
  | {
      type: ActionType.SET_SCHEDULES
      payload: { data: Schedule[]; pagination: Pagination | null }
    }
  | {
      type: ActionType.SET_TEAMS
      payload: { data: Team[]; pagination: Pagination | null }
    }
  | {
      type: ActionType.SET_VENUES
      payload: { data: Venue[]; pagination: Pagination | null }
    }
  | {
      type: ActionType.SET_GAMES
      payload: { games: Game[]; pagination: Pagination | null }
    }
  | { type: ActionType.SET_LOADING_GAMES; payload: boolean }
  | { type: ActionType.SET_LOADING_ORGANIZATIONS; payload: boolean }
  | { type: ActionType.SET_LOADING_SCHEDULES; payload: boolean }
  | { type: ActionType.SET_LOADING_TEAMS; payload: boolean }
  | { type: ActionType.SET_LOADING_VENUES; payload: boolean }
  | { type: ActionType.SET_LOADING_DATES; payload: boolean }
  | {
      type: ActionType.SET_DATES
      payload: {
        dates: string[]
        hasTodayGames: boolean
        hasUpcomingGames: boolean
        defaultTimePeriod?: TimePeriod
      }
    }
  | {
      type: ActionType.CLEAR_FILTER
      payload:
        | 'organization'
        | 'schedule'
        | 'team'
        | 'venue'
        | 'facility'
        | 'dateRange'
        | 'timePeriod'
    }
  | { type: ActionType.CLEAR_ALL_FILTERS }
  | { type: ActionType.SET_FILTERS_LOADED; payload: boolean }
  | {
      type: ActionType.APPEND_GAMES
      payload: { games: Game[]; pagination: Pagination | null }
    }
  | { type: ActionType.SET_SELECTED_MEMBERSHIP_TYPE; payload: MembershipType }
  | {
      type: ActionType.APPEND_SCHEDULES
      payload: { data: Schedule[]; pagination: Pagination | null }
    }
  | {
      type: ActionType.APPEND_TEAMS
      payload: { data: Team[]; pagination: Pagination | null }
    }
  | {
      type: ActionType.APPEND_VENUES
      payload: { data: Venue[]; pagination: Pagination | null }
    }
  | { type: ActionType.SET_APPENDING_SCHEDULES; payload: boolean }
  | { type: ActionType.SET_APPENDING_TEAMS; payload: boolean }
  | { type: ActionType.SET_APPENDING_VENUES; payload: boolean }
  | { type: ActionType.SET_SCHEDULES_SEARCH_QUERY; payload: string }
  | { type: ActionType.SET_TEAMS_SEARCH_QUERY; payload: string }
  | { type: ActionType.SET_VENUES_SEARCH_QUERY; payload: string }
  | { type: ActionType.SET_SELECTED_SORT_ORDER; payload: SortOrder }
  | { type: ActionType.SET_CALENDAR_MODE; payload: boolean }

// Context interface
export interface MyScheduleContextValue extends MyScheduleState {
  // Setters
  setSelectedOrganization: (org: Organization | null) => void
  setSelectedSchedule: (schedule: Schedule | null) => void
  setSelectedTeam: (team: Team | null) => void
  setSelectedVenue: (venue: Venue | null) => void
  setSelectedFacility: (facility: Facility | null) => void
  setSelectedDateRange: (range: [Dayjs, Dayjs] | null) => void
  /**
   * The query the games request sends — filters plus the date window. Shared
   * with the events request so both lists are bounded identically.
   */
  scheduleQuery: Record<string, unknown>
  setSelectedTimePeriod: (period: TimePeriod) => void
  setSelectedMembershipType: (type: MembershipType) => void
  setSelectedSortOrder: (order: SortOrder) => void
  setCalendarMode: (mode: boolean) => void

  // Data fetchers
  fetchGames: () => Promise<void>
  fetchGamesPage: (page: number) => Promise<void>
  fetchMoreGames: () => Promise<void>
  fetchOrganizationsTree: () => Promise<void>
  fetchSchedules: (search?: string) => Promise<void>
  fetchTeams: (search?: string) => Promise<void>
  fetchVenues: (search?: string) => Promise<void>

  // Pagination fetchers for filter dropdowns
  fetchMoreSchedules: () => Promise<void>
  fetchMoreTeams: () => Promise<void>
  fetchMoreVenues: () => Promise<void>

  // Search handlers for filter dropdowns
  searchSchedules: (query: string) => void
  searchTeams: (query: string) => void
  searchVenues: (query: string) => void

  // Clear functions
  clearFilter: (
    filterType:
      | 'organization'
      | 'schedule'
      | 'team'
      | 'venue'
      | 'facility'
      | 'dateRange'
      | 'timePeriod'
  ) => void
  clearAllFilters: () => void
}
